<template>
  <div class="ai-assistant-wrapper" :class="{ loading: loading }">
    <div class="content-wrap">
      <div class="conversation">
        <overlay-scrollbars ref="ai-conversation" :options="{ scrollbars: { autoHide: 'leave' }, overflowBehavior: { y: 'scroll', x: 'scroll' } }">
          <div class="content">
            <div v-for="(message, index) in conversation" :key="index" class="message" :class="['message--' + message.author]">
              <div class="message--avatar">
                <div v-if="(message.author === 'ai')"><font-awesome-icon icon="fa-solid fa-robot" /></div>
                <Avatar v-if="(message.author === 'user')" :userId="currentUser.id" />
              </div>
              <div class="message--text" :class="{ pending: !message.message }">
                <span class="message-tail">
                  <svg v-if="(message.author === 'ai')" viewBox="0 0 8 13" height="13" width="8" preserveAspectRatio="xMidYMid meet" class="" version="1.1" x="0px" y="0px" enable-background="new 0 0 8 13" xml:space="preserve"><path opacity="0.13" fill="#0000000" d="M1.533,3.568L8,12.193V1H2.812 C1.042,1,0.474,2.156,1.533,3.568z"></path><path fill="currentColor" d="M1.533,2.568L8,11.193V0L2.812,0C1.042,0,0.474,1.156,1.533,2.568z"></path></svg>
                  <svg v-if="(message.author === 'user')" viewBox="0 0 8 13" height="13" width="8" preserveAspectRatio="xMidYMid meet" class="" version="1.1" x="0px" y="0px" enable-background="new 0 0 8 13" xml:space="preserve"><path opacity="0.13" d="M5.188,1H0v11.193l6.467-8.625 C7.526,2.156,6.958,1,5.188,1z"></path><path fill="currentColor" d="M5.188,0H0v11.193l6.467-8.625C7.526,1.156,6.958,0,5.188,0z"></path></svg>
                </span>
                <div v-if="message.message" class="message--text__content" v-html="message.message" />
                <div v-else>
                  <svg id="dots" width="32px" height="32px" viewBox="0 0 132 58" version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                      <g id="dots" fill="#3333337d">
                        <circle id="dot1" cx="25" cy="30" r="13"></circle>
                        <circle id="dot2" cx="65" cy="30" r="13"></circle>
                        <circle id="dot3" cx="105" cy="30" r="13"></circle>
                      </g>
                    </g>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </overlay-scrollbars>
      </div>
      <div class="prompt-container">
        <div class="prompt">
          <b-form-textarea
            v-model="promptMessage"
            :placeholder="$gettext('Send a message.')"
            @keydown="sendPromptOnEnter"
            rows="4"
            no-resize
            :maxlength="maxPromptLength"
          />
          <GekkoButton 
            @click="sendMessage"
            :text="$gettext('Send')" 
            class="send-message-btn"
            :disabled="!promptMessage"
          >
            <template v-slot:icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" class="svg-inline--fa" stroke-width="2"><path d="M.5 1.163A1 1 0 0 1 1.97.28l12.868 6.837a1 1 0 0 1 0 1.766L1.969 15.72A1 1 0 0 1 .5 14.836V10.33a1 1 0 0 1 .816-.983L8.5 8 1.316 6.653A1 1 0 0 1 .5 5.67V1.163Z" fill="currentColor"></path></svg>
            </template>
          </GekkoButton>
          <div class="prompt__text-count" :class="{ maxfill: promptMessage.length >= maxPromptLength }">{{ promptMessage.length }}/{{ maxPromptLength }}</div>
        </div>
        <div class="disclaimer">{{ this.disclaimer }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import GekkoButton from "@/components/GekkoButton";
import GkLogo from "@/components/ui/GkLogo.vue";
import Avatar from "@/components/Avatar";
import AiAssistantService from "@/store/services/ai.assistant.service.js";

export default {
  name: 'ai-assistant',
  components: { 
    GekkoButton,
    GkLogo,
    Avatar
  },
  props: {
    initialize: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      loading: true,
      promptMessage: '',
      waitingForResponse: false,
      maxPromptLength: null,
      disclaimer: null,
      intro: null,
      botId: null,
      initialized: false,
      conversation: []
    }
  },
  computed: {
    ...mapGetters ({ 
      currentUser: 'main/getCurrentUser',
      isPremium: 'getIsPremium'
    })
  },
  watch: {
    initialize (val) {
      if (!this.initialized && val) {
        this.initialized = true
        setTimeout(() => {
          this.initializeConversation()
        }, 300)
      }
    }
  },
  created () {
    // conversation scroll bottom
    this.scrollConversation()
    AiAssistantService.getCurrentChatBot().then((response) => {
      this.maxPromptLength = response.data.max_prompt_length
      this.disclaimer = response.data.disclaimer
      this.intro = response.data.intro
      this.botId = response.data.id
    });
  },
  methods: {
    initializeConversation () {
      if (!this.conversation.map(c => c.message).includes(this.intro)) {
        this.conversation.push({ author: 'ai', message: null });
        setTimeout(() => {
          this.fillResponseMessage(this.intro)
        }, 500);
      }
    },
    sendPromptOnEnter (e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (this.promptMessage && !!this.promptMessage.trim()) {
          this.sendMessage()
        } else {
          this.promptMessage = null
        }
      }
    },
    sendMessage () {
      if (this.waitingForResponse) return

      this.conversation.push({
        author: 'user',
        message: this.promptMessage
      })
      this.scrollConversation()

      // trim prompt length to maxPromptLength
      const requestQuestion = this.promptMessage.substring(0, this.maxPromptLength)

      this.promptMessage = '' // reset textarea
      this.waitingForResponse = true

      setTimeout(() => { // TODO: remove timeout?
        this.conversation.push({
          author: 'ai',
          message: null // to show dots
        })
        this.scrollConversation()

        let messageData = {
          message: requestQuestion,
          bot_id: this.botId
        }
        
        AiAssistantService.getResponse(messageData).then((response) => {
          this.waitingForResponse = false;
          this.fillResponseMessage(response.data.reply);

          window.mixpanel.track("Gekkobot: success");
        }).catch((error) => {
          this.waitingForResponse = false
          let premsg = this.$gettext('Failed to process your message: ')
          let msg = this.$gettext('GekkoBot not responding.')
          if (error.status === 403) {
            premsg = this.$gettext('You\'ve reached your limit of free consultations: ')
            msg = this.$gettext('Please consider upgrading to Premium to keep using GekkoBot.')
            this.$store.commit('togglePremiumModal', 'web_ai_assistant')

            window.mixpanel.track("Gekkobot: error - limit of free consultations");
          } else {
            if (error.status === 405) {
              msg = this.$gettext('You are not allowed to use the chat bot feature.')
              window.mixpanel.track("Gekkobot: error - not allowed");
            } else if (error.status === 429) {
              msg = this.$gettext('You have already sent too many messages.')
              window.mixpanel.track("Gekkobot: error - too many messages");
            }
            this.$store.commit('setAlert', {
                title: this.$gettext('Error'),
                text: msg,
                variant: 'error',
                duration: 4
            })
          }
          this.fillResponseMessage(premsg + msg);
        })
      }, 200)
    },
    fillResponseMessage (response) {
      const response_item = this.conversation[this.conversation.length - 1]
      if (response_item.author === 'ai' && !response_item.message) {
        response_item.message = this.parseGPTResponse(response);
        this.scrollConversation();
      }
    },
    parseGPTResponse (message) {
      let parsed_message = message

      // Replace new lines with <br> tags
      parsed_message = parsed_message.replace(/\n/g, '<br>')

      // Split the generated text into paragraphs
      const paragraphs = parsed_message.split('<br><br>');

      // Wrap each paragraph in <p> tags
      parsed_message = paragraphs.map(paragraph => `<p>${paragraph}</p>`).join('');

      return parsed_message
    },
    scrollConversation () {
    // conversation scroll bottom
      this.$nextTick(() => {
        const osInstance = this.$refs['ai-conversation'].osInstance();
        osInstance.scroll({
          y: '100%'
        }, 300);
      })
    },
    resetContext () {
      if (this.conversation.length < 2) {
        return
      }
      this.loading = true
      AiAssistantService.resetContext().then(() => {
        this.conversation = this.conversation.filter(obj => obj.message == this.intro);
        this.loading = false;
        this.waitingForResponse = false;
      }).catch(() => {
        this.$store.commit('setAlert', {
          title: this.$gettext("Error"),
          text: this.$gettext("We could not reset the conversation."),
          variant: "error",
          duration: 4
        });
      });
    }
  }
}
</script>

<style lang="scss">
.ai-assistant-wrapper {
  .message--text__content {
    p:last-child { margin-bottom: 0; }
  }
}
</style>
<style lang="scss" scoped>
.ai-assistant-wrapper {
  width: 100%; height: 100%;
  transition: all 0.3s ease;
  color: $colorTextBase;
  font-size: 1rem;
  .content-wrap { 
    height: 100%; width: 100%; 
    display: flex;
    flex-direction: column;
    transition: opacity 0.3s ease;

    .conversation {
      width: 100%;
      flex: 1;
      overflow: hidden;
      .os-host { height: 100%; }
      .content {
        display: block;
        height: 100%;
        padding: 0.5rem;
        .message {
          display: flex;
          gap: 0.75rem;
          padding: 0.5rem;
          &--user {
            flex-flow: row-reverse;
            .message--text {
              background: $colorChatbotMsgUser !important;
              border-top-right-radius: 0;
              .message-tail {
                color: $colorChatbotMsgUser;
                right: -8px;
              }
            }
          }
          &--ai {
            .message--avatar {
              border-radius: 100%;
              background: $colorBrand;
              color: white;
              font-size: 1rem;
            }
            .message--text {
              border-top-left-radius: 0;
              &:not(.pending) {
                background: $colorChatbotMsgBot !important;
              }
              .message-tail {
                color: $colorChatbotMsgBot;
                left: -8px;
              }
            }
          }
          &--avatar {
            width: 36px;
            height: 36px;
            min-width: 36px;
            min-height: 36px;
            background: $chatbotPanelBg;
            border-radius: 50em;
            box-shadow: 0 2px 4px rgba(0,0,0,.1);
            display: flex;
            align-items: center;
            justify-content: center;
          }
          &--text {
            flex: 1;
            border-radius: $radiusSm;
            font-size: 14px;
            position: relative;
            .message-tail {
              position: absolute;
              top: 0;
              z-index: 100;
              display: none;
              width: 8px;
              height: 13px;
              svg { display: block; pointer-events: none; }
            }
            &__content {
              padding: 0.5rem;
            }
            &:not(.pending) {
              box-shadow: 0 2px 4px rgba(0,0,0,.1);
              .message-tail { display: block; }
            }
          }
        }
      }
    }
    .disclaimer {
      color: #a4a4a4;
      text-align: center;
      font-size: 12px;
      padding: 0.5rem;
      padding-bottom: 0;
    }
    .prompt-container {
      display: flex;
      flex-direction: column;
      padding: 0.5rem;
      background: $chatbotPanelBg;
      .prompt {
        width: 100%;
        position: relative;
        &__text-count {
          position: absolute;
          bottom: 0.25rem;
          left: 0.5rem;
          font-size: 14px;
          opacity: 0.3;
          &.maxfill {
            color: $colorDanger;
            opacity: 0.8;
          }
        }
        .form-control {
          &:placeholder {
            color: $colorTextBase;
            opacity: 0.5;
          }
          &:focus { box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075); }
        }
        textarea {
          background: white;
          color: $colorTextBase;
          border: none;
        }
        .send-message-btn {
          position: absolute;
          bottom: 0.25rem;
          right: 0.25rem;
          background: none;
          transition: none;
          border: none;
          border-radius: $radiusSm;
          &.disabled { color: $colorTextBase !important; }
          &:not(.disabled) { background: $colorBrand; }
          &:hover { background: $colorBrand800; }
        }
      }
    }
  }
}
</style>
<style lang="scss">
.ai-assistant-wrapper {
  .os-host {
    .os-content { padding: 0 !important; }
  }
  .avatar {
    margin: 0;
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    box-shadow: 0 2px 4px rgba(0,0,0,.1);
  }
  .send-message-btn {
    span {
      display: inline-block;
      overflow: hidden;
      max-width: 50px;
      transition: max-width 0.5s cubic-bezier(0, 0, 0.2, 1);
      vertical-align: bottom;
    }
    &.disabled {
      span { max-width: 0; }
      &:hover { background: none !important; }
      .svg-inline--fa { margin-right: 0; }
    }
  }

  #dots #dot1{
    animation: load 1s infinite;
  }

  #dots #dot2{
    animation: load 1s infinite;
    animation-delay: 0.2s;
  }

  #dots #dot3{
    animation: load 1s infinite;
    animation-delay: 0.4s;
  }

  @keyframes load{
    0% { opacity: 0; }
    50% { opacity: 1; }
    100% { opacity: 0; }
  }


}
</style>